import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Product } from '../types/product-variant';
import { parseLocalePath } from '../i18n/routes';

interface ProductsContextType {
  products: Product[];
  loading: boolean;
  error: string | null;
  getProductBySlug: (slug: string) => Product | undefined;
  getProductById: (id: string) => Product | undefined;
}

const ProductsContext = createContext<ProductsContextType | null>(null);

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

export const ProductsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hadPreloaded = useRef(false);

  const [products, setProducts] = useState<Product[]>(() => {
    const preloaded = (window as any).__PRELOADED_DATA__?.products;
    if (!preloaded) return [];
    hadPreloaded.current = true;
    delete (window as any).__PRELOADED_DATA__.products;
    const allProducts: Product[] = (preloaded || []).map((p: any) => ({
      id: p.id,
      airtableId: p.airtableId,
      name: p.name,
      slug: p.slug,
      hasVariants: p.hasVariants,
      price: p.price,
      basePrice: p.basePrice,
      description: p.description || '',
      'SEO-description': p['SEO-description'],
      material: p.material,
      category: p.category,
      collection: p.collection,
      oneOfKind: p.oneOfKind || false,
      variants: p.variants || [],
      inStock: p.inStock,
      status: p.status,
      createdAt: p.createdAt,
    }));
    return allProducts.filter(product => product.status !== 'draft');
  });
  const [loading, setLoading] = useState(() => !hadPreloaded.current);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const locale = parseLocalePath(location.pathname).locale;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Don't flash loading state if we have preloaded data for this first render
        const skipLoading = hadPreloaded.current;
        hadPreloaded.current = false;
        if (!skipLoading) setLoading(true);

        const response = await fetch(`/api/data?type=products&locale=${locale}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.statusText}`);
        }

        const data = await response.json();

        // API returns pre-parsed data — no JSON string parsing needed
        const allProducts: Product[] = (data.products || []).map((p: any) => ({
          id: p.id,
          airtableId: p.airtableId, // record_id from Postgres, used for media lookups
          name: p.name,
          slug: p.slug,
          hasVariants: p.hasVariants,
          price: p.price,
          basePrice: p.basePrice,
          description: p.description || '',
          'SEO-description': p['SEO-description'],
          material: p.material,
          category: p.category,
          collection: p.collection,
          oneOfKind: p.oneOfKind || false,
          variants: p.variants || [],
          inStock: p.inStock,
          status: p.status,
          createdAt: p.createdAt,
        }));

        // Only hide drafts; keep all products otherwise
        const visibleProducts = allProducts.filter(product => product.status !== 'draft');

        setProducts(visibleProducts);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [locale]);

  const getProductBySlug = (slug: string): Product | undefined => {
    return products.find(p => p.slug === slug);
  };

  const getProductById = (id: string): Product | undefined => {
    return products.find(p => p.id === id);
  };

  return (
    <ProductsContext.Provider value={{ products, loading, error, getProductBySlug, getProductById }}>
      {children}
    </ProductsContext.Provider>
  );
};

export default ProductsContext;
